import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import FullCalendar from '@fullcalendar/react'
import { EventContentArg } from '@fullcalendar/core'
import jaLocale from '@fullcalendar/core/locales/ja'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import scrollGridPlugin from '@fullcalendar/scrollgrid'

import EventContent from 'components/EventContent'
import { Loading } from 'components/Loading'
import { getColor, InputClass } from 'contexts/style'
import { JSTTime } from 'contexts/dateUtils'
import { useFindBookings } from 'contexts/useMongoQuery'
import { ScheduleViewData } from './scheduleView'

/**
 * 予約候補日検索の候補を、その日の予約表（ドック予約画面と同じカレンダー）に重ねて表示する確認用ページ
 * 予約の登録はしない。候補は URL の data（JSON）で受け取る
 */

// ドック予約画面と同じ並びの予約枠（候補の検査があるものだけ表示）
const RESOURCE_ORDER = ['ABUS', 'ES', 'MMG', 'MRI', 'PET-CT_1', 'PET-CT_2', 'US', 'US(L)']

const getTimeUnit = (value: string) => `${value === '60' ? '01:00' : '00:' + ('0' + value).slice(-2)}:00`

// 候補の検査は既存の予約と区別できる色で表示する（黄色の背景なので文字は濃い色）
const CANDIDATE_STYLE = { backgroundColor: '#f1d063' }
const CandidateEventContent = (arg: EventContentArg) => arg.event.extendedProps.candidate ? (
  <div className="fc-event-main-frame text-gray-900 border-2 border-dashed border-white h-full" style={CANDIDATE_STYLE}>
    <div className="px-2">候補 {arg.event.extendedProps.patientName}</div>
  </div>
) : EventContent(arg)

const parseData = (value: string | null): ScheduleViewData | undefined => {
  try {
    const data = value ? JSON.parse(value) : undefined
    return data?.date && Array.isArray(data.persons) ? data : undefined
  } catch (e) {
    return undefined
  }
}

const CandidateSchedule = () => {
  const [searchParams] = useSearchParams()
  const data = useMemo(() => parseData(searchParams.get('data')), [searchParams])
  const [timeUnit, setTimeUnit] = useState('5')

  const date = useMemo(() => (data ? new Date(`${data.date}T00:00:00`) : undefined), [data])
  const dateRange = useMemo(() => {
    if (!date) return { start: null, end: null }
    const end = new Date(date)
    end.setDate(end.getDate() + 1)
    return { start: date, end }
  }, [date])
  const { loading, data: events } = useFindBookings(dateRange)

  const candidateEvents = useMemo(() => (data?.persons ?? []).flatMap((person, i) => person.exams.map((exam, k) => ({
    id: `candidate-${i}-${k}`,
    title: '候補',
    start: new Date(exam.start),
    end: new Date(exam.end),
    resourceId: exam.modalityID,
    editable: false,
    extendedProps: { candidate: true, patientName: `${person.label}(${exam.body2})` },
  }))), [data])

  const resources = useMemo(() => {
    const used = new Set(candidateEvents.map(v => v.resourceId))
    // PET-CT は1号機・2号機の両方を表示する
    if (used.has('PET-CT_1') || used.has('PET-CT_2')) ['PET-CT_1', 'PET-CT_2'].forEach(v => used.add(v))
    return RESOURCE_ORDER.filter(v => used.has(v)).map(id => ({ id, title: id }))
  }, [candidateEvents])

  if (!data || !date) {
    return <div className="p-10 text-red-600">候補の情報を読み込めませんでした。予約候補日検索の画面から開き直してください。</div>
  }

  return (
    <div className="p-4 space-y-3 text-sm">
      {loading && <Loading full />}
      <div className="flex items-center gap-4 flex-wrap">
        <h2 className="text-lg font-bold">{data.title}</h2>
        <span className="text-gray-500">確認用の表示です。予約は登録されません。</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="px-2 text-gray-900 border-2 border-dashed border-white" style={CANDIDATE_STYLE}>候補</label>
        <label className={`px-2 text-white bg-${getColor(0)}`}>外来</label>
        <label className={`px-2 text-white bg-${getColor(1)}`}>外注</label>
        <label className={`px-2 text-white bg-${getColor(2)}`}>通常ドック</label>
        <label className={`px-2 text-white bg-${getColor(6)}`}>CMC・VIP</label>
        <label className="pl-4 inline-flex items-center gap-2">
          目盛
          <select className={InputClass + ' h-9 py-1'} value={timeUnit} onChange={e => setTimeUnit(e.target.value)}>
            <option value="5">5分</option>
            <option value="20">20分</option>
            <option value="60">60分</option>
          </select>
        </label>
      </div>

      <div className="space-y-1">
        {data.persons.map(person => (
          <div key={person.label}>
            <span className="font-medium mr-2">{person.label}</span>
            {person.reception && <span className="font-medium mr-3">受付 {JSTTime(new Date(person.reception))}</span>}
            {person.exams.map(exam => `${JSTTime(new Date(exam.start))}〜${JSTTime(new Date(exam.end))} ${exam.modalityID} ${exam.body2}${exam.ivCheckin ? `（IV ${exam.ivCheckin}）` : ''}`).join(' / ')}
          </div>
        ))}
      </div>

      <FullCalendar
        locale={jaLocale}
        plugins={[resourceTimeGridPlugin, scrollGridPlugin]}
        views={{ resourceTimeGridOneDay: { type: 'resourceTimeGrid', duration: { days: 1 } } }}
        headerToolbar={{ start: '', center: 'title', end: '' }}
        initialDate={date}
        initialView="resourceTimeGridOneDay"
        editable={false}
        allDaySlot={false}
        dayMinWidth={96}
        slotMinTime="08:00:00"
        slotMaxTime="19:00:00"
        slotDuration={getTimeUnit(timeUnit)}
        slotLabelFormat={(arg) => arg.date.minute ? String(arg.date.minute) : (String(arg.date.hour) + ':00')}
        slotLabelInterval={getTimeUnit(timeUnit)}
        datesAboveResources
        eventColor="lightgray"
        resources={resources}
        events={[...(events ?? []), ...candidateEvents] as any}
        eventContent={CandidateEventContent}
      />
    </div>
  )
}

export default CandidateSchedule
